import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import { Checkbox, PromptModal } from '@/components'
import { Input } from '@/components_deprecated'

export interface IssueRelationOptionFormItem {
  id?: string
  name: string
}

export interface IssueRelationForm {
  name: string
  required: boolean
  options: IssueRelationOptionFormItem[]
}

interface ExistingRelationSummary {
  id: string
  name: string
}

interface IssueRelationPromptProps {
  initialData?: IssueRelationForm
  relationId?: string
  existingRelations: ExistingRelationSummary[]
  onSubmit: PromptResolve<IssueRelationForm | null>
}

export const IssueRelationPrompt: React.FC<IssueRelationPromptProps> = ({
  initialData,
  relationId,
  existingRelations,
  onSubmit,
}) => {
  const translations = useTranslate({
    addIssueRelation: 'settings.add-issue-relation',
    editIssueRelation: 'settings.edit-issue-relation',

    name: 'common.name',
    required: 'common.required',
    options: 'settings.issue-relation-options',
    addOption: 'settings.add-issue-relation-option',
    optionName: 'settings.issue-relation-option-name',
    removeOption: 'common.remove',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      required: 'common.required',
      nameExists: 'settings.validation.issue-relation-name-exists',
      duplicateOption: 'settings.validation.issue-relation-option-duplicate',
      noOptions: 'settings.validation.issue-relation-no-options',
    },
  })

  const editing = typeof initialData !== 'undefined'

  function validateName(name: string): string | null {
    if (name === '') return translations.validation.required
    const clash = existingRelations.find(
      relation => relation.name === name && relation.id !== relationId
    )
    if (clash) return translations.validation.nameExists
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formChangeHandler: handler,
    submitHandler: submit,
    updateForm,
  } = useForm<IssueRelationForm>({
    values: initialData ?? { name: '', required: false, options: [] },
    validators: {
      name: val => validateName(val),
      options: val => {
        if (val.length === 0) return translations.validation.noOptions
        const names = val.map(o => o.name.trim()).filter(Boolean)
        if (new Set(names).size !== names.length)
          return translations.validation.duplicateOption
        if (names.length !== val.length) return translations.validation.required
        return null
      },
    },
  })

  const [newOptionName, setNewOptionName] = useState('')

  function addOption() {
    const trimmed = newOptionName.trim()
    if (trimmed === '') return
    if (form.options.some(o => o.name === trimmed)) return
    updateForm({ options: [...form.options, { name: trimmed }] })
    setNewOptionName('')
  }

  function optionsEqual(
    a: IssueRelationOptionFormItem[],
    b: IssueRelationOptionFormItem[]
  ): boolean {
    if (a.length !== b.length) return false
    return a.every((opt, i) => opt.id === b[i]?.id && opt.name === b[i]?.name)
  }

  const isDirty =
    !editing ||
    form.name !== initialData?.name ||
    form.required !== initialData.required ||
    !optionsEqual(form.options, initialData.options) ||
    newOptionName.trim() !== ''

  function handleSubmit(values: IssueRelationForm) {
    const trimmed = newOptionName.trim()
    if (trimmed !== '' && !values.options.some(o => o.name === trimmed)) {
      onSubmit({
        ...values,
        options: [...values.options, { name: trimmed }],
      })
      return
    }
    onSubmit(values)
  }

  function removeOption(idx: number) {
    updateForm({ options: form.options.filter((_, i) => i !== idx) })
  }

  function updateOptionName(idx: number, name: string) {
    updateForm({
      options: form.options.map((option, i) =>
        i === idx ? { ...option, name } : option
      ),
    })
  }

  return (
    <PromptModal
      title={
        editing ? translations.editIssueRelation : translations.addIssueRelation
      }
      submitText={editing ? translations.update : translations.create}
      submitDisabled={
        errors.name != null ||
        form.name === '' ||
        (form.options.length === 0 && newOptionName.trim() === '') ||
        !isDirty
      }
      className="w-[480px]"
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      onSubmit={submit(handleSubmit)}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={event => {
          event.preventDefault()
          submit(handleSubmit)()
        }}
      >
        <div className="flex flex-col">
          <label className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.name}
          </label>
          <Input
            value={form.name}
            error={errors.name}
            autoFocus
            fullWidth
            onChange={handler('name')}
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="issue-relation-required"
            className="flex cursor-pointer flex-col items-start gap-2 text-sm font-semibold text-tertiary"
          >
            <span>{translations.required}</span>
            <Checkbox
              id="issue-relation-required"
              value={form.required}
              onChange={checked => updateForm({ required: checked === true })}
            />
          </label>
        </div>
        <div className="flex flex-col">
          <label className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.options}
          </label>
          {form.options.length > 0 && (
            <ul className="m-0 mb-3 flex list-none flex-col gap-2 p-0">
              {form.options.map((option, idx) => (
                <li
                  key={option.id ?? `new-${idx}`}
                  className="flex items-center gap-2"
                >
                  <Input
                    value={option.name}
                    fullWidth
                    onChange={value => updateOptionName(idx, value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    aria-label={translations.removeOption}
                    className="flex h-10 w-10 items-center justify-center rounded hover:bg-quaternary"
                  >
                    <Icon icon="times" color="gray6" hoverColor="red" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            <Input
              value={newOptionName}
              fullWidth
              placeholder={translations.optionName}
              onChange={setNewOptionName}
              onEnter={evt => {
                evt.preventDefault()
                addOption()
              }}
            />
            <button
              type="button"
              onClick={addOption}
              aria-label={translations.addOption}
              disabled={newOptionName.trim() === ''}
              className="flex h-10 w-10 items-center justify-center rounded bg-secondary text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon icon="plus" color="white" />
            </button>
          </div>
          {errors.options && (
            <span className="text-red mt-2 text-sm">{errors.options}</span>
          )}
        </div>
      </form>
    </PromptModal>
  )
}
